import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  setDoc
} from "firebase/firestore";
import { User } from "@/types";
import { db } from "./config";

export const getUserById = async (userId: string) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as User;
    }
    return null;
  } catch (error) {
    console.error("Error getting user: ", error);
    throw error;
  }
};

export const getUserByEmail = async (email: string) => {
  try {
    const usersRef = collection(db, "users");
    const q = query(
      usersRef,
      where("username", "==", email)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // Return the first user found with this email
    const userDoc = querySnapshot.docs[0];
    return { id: userDoc.id, ...userDoc.data() } as User;
  } catch (error) {
    console.error("Error getting user by email: ", error);
    // Instead of throwing, return null so the flow can continue
    return null;
  }
};

export const getChildrenByFamilyId = async (familyId: string) => {
  try {
    const q = query(
      collection(db, "users"),
      where("familyId", "==", familyId),
      where("role", "==", "child")
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    })) as User[];
  } catch (error) {
    console.error("Error getting children: ", error);
    throw error;
  }
};

export const getFamilyMembersByFamilyId = async (familyId: string) => {
  try {
    const q = query(
      collection(db, "users"),
      where("familyId", "==", familyId)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    })) as User[];
  } catch (error) {
    console.error("Error getting family members: ", error);
    throw error;
  }
};

// Updated updateUserAuthId to handle the actual user document update
export const updateUserAuthId = async (tempUserId: string, authId: string) => {
  try {
    console.log("Linking temporary user:", tempUserId, "to auth ID:", authId);
    
    // Get the temporary user data
    const tempUserDoc = await getDoc(doc(db, "users", tempUserId));
    if (!tempUserDoc.exists()) {
      throw new Error("Temporary user not found");
    }
    
    const tempUserData = tempUserDoc.data();
    console.log("Found temp user data:", tempUserData);
    
    // Create the new user document with the Firebase auth ID
    const newUserData = {
      ...tempUserData,
      id: authId,  // Use id field instead of authId
      isTemporary: false,
      linkedFrom: tempUserId,
      updatedAt: serverTimestamp()
    };
    delete newUserData.isTemporary;
    
    // Create the new user document using auth ID as document ID
    await setDoc(doc(db, "users", authId), newUserData);
    console.log("Created new user document with auth ID:", authId);
    
    // Update any transactions that reference the temporary user ID
    const { updateTransactionUserId } = await import("./transactions");
    await updateTransactionUserId(tempUserId, authId);
    
    // Clean up: delete the temporary user
    await deleteDoc(doc(db, "users", tempUserId));
    console.log("Deleted temporary user:", tempUserId);
    
    return true;
  } catch (error) {
    console.error("Error updating user auth ID: ", error);
    throw error;
  }
};

export const updateChild = async (
  childId: string,
  firstName: string,
  lastName: string
) => {
  try {
    await updateDoc(doc(db, "users", childId), {
      firstName,
      lastName,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error updating child: ", error);
    throw error;
  }
};

export const addChild = async (
  familyId: string,
  firstName: string,
  lastName: string,
  username: string,
  role: 'child' | 'parent'
) => {
  try {
    console.log("Starting addChild with data:", { familyId, firstName, lastName, username, role });
    
    // Generate a temporary ID for the user
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log("Generated temp ID:", tempId);
    
    // Try using setDoc with a specific document ID instead of addDoc
    const childRef = doc(db, "users", tempId);
    console.log("About to write to Firestore with doc ref:", childRef.path);
    
    const userData = {
      familyId,
      firstName,
      lastName,
      username,  // This is the email address
      image: "",
      role,
      balance: 0,
      isTemporary: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    console.log("User data to write:", userData);
    
    await setDoc(childRef, userData);
    
    console.log("Successfully added user with ID:", tempId);
    return tempId;
  } catch (error) {
    console.error("Error in addChild function:", error);
    console.error("Error details:", {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

export const removeChild = async (childId: string) => {
  try {
    // First delete all transactions associated with this child
    const { deleteTransactionsByUserId } = await import("./transactions");
    await deleteTransactionsByUserId(childId);
    
    // Then delete the child
    await deleteDoc(doc(db, "users", childId));
    
    return true;
  } catch (error) {
    console.error("Error removing child: ", error);
    throw error;
  }
};
